import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React, { Component, ErrorInfo, ReactNode } from 'react';

import { isDebugEnabled, isDevelopment } from '../utils/config';
import { AXON_LOGO_PATH, getContactSupportMessage, getErrorBoundaryTitle, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../utils/constants';
import { logger } from '../utils/logger';

// Create component logger for error boundary
const errorBoundaryLogger = logger.withComponent('ErrorBoundary');

/**
 * Props for the ErrorBoundary component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  supportPhoneDisplay?: string;
}

/**
 * State for the ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

/**
 * React Error Boundary component that catches JavaScript errors anywhere in the component tree
 * Provides user-friendly error messages and fallback UI
 * Includes error reporting/logging capabilities
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  /**
   * Static method called when an error occurs
   * Updates state to show fallback UI
   * @param error - The error that occurred
   * @returns Updated state
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  /**
   * Called after an error has been thrown by a descendant component
   * Used for error reporting and logging
   * @param error - The error that was thrown
   * @param errorInfo - Information about the error
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      errorInfo,
    });

    // Log error to console in development
    if (isDevelopment()) {
      errorBoundaryLogger.error('ErrorBoundary caught an error:', error);
      errorBoundaryLogger.error('Error info:', errorInfo);
    }

    // Report error to external service or custom handler
    this.reportError(error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reports error to external monitoring service
   * In a real application, this would send to services like Sentry, LogRocket, etc.
   * @param error - The error that occurred
   * @param errorInfo - Additional error information
   */
  private reportError = (error: Error, errorInfo: ErrorInfo): void => {
    const errorReport = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: null, // Add user identification if available
    };

    // In development, just log to console
    if (isDevelopment()) {
      errorBoundaryLogger.warn('Error report:', errorReport);
      return;
    }

    // In production, send to monitoring service
    try {
      // Example: Send to error reporting service
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport),
      // });

      // For now, just store in localStorage for debugging
      const existingErrors = JSON.parse(localStorage.getItem('error_reports') || '[]');
      existingErrors.push(errorReport);
      
      // Keep only last 10 errors to prevent storage bloat
      if (existingErrors.length > 10) {
        existingErrors.splice(0, existingErrors.length - 10);
      }
      
      localStorage.setItem('error_reports', JSON.stringify(existingErrors));
    } catch (reportingError) {
      errorBoundaryLogger.error('Failed to report error:', reportingError);
    }
  };

  /**
   * Handles the retry action when user clicks retry button
   */
  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  /**
   * Handles page reload action
   */
  private handleReload = (): void => {
    window.location.reload();
  };

  /**
   * Copies error details to clipboard for debugging
   */
  private copyErrorDetails = async (): Promise<void> => {
    try {
      const errorDetails = {
        errorId: this.state.errorId,
        message: this.state.error?.message,
        stack: this.state.error?.stack,
        componentStack: this.state.errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };

      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      alert(__('Error details copied to clipboard'));
    } catch (err) {
      errorBoundaryLogger.error('Failed to copy error details:', err);
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: '#f5f5f5',
          }}
        >
          <Box
            sx={{
              maxWidth: 600,
              width: '100%',
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              p: 4,
            }}
          >
            {/* Axon Logo */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                component="img"
                src={AXON_LOGO_PATH}
                alt="Axon Logo"
                sx={{
                  height: 40,
                  width: 'auto',
                }}
              />
            </Box>

            <Stack spacing={3}>
              {/* Error Alert */}
              <Alert severity="error">
                <Typography variant="h6" gutterBottom>
                  {getErrorBoundaryTitle()}
                </Typography>
                <Typography variant="body2">
                  {__('We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.')}
                </Typography>
                {this.state.errorId && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {__('Error ID: $[errorId]', { errorId: this.state.errorId })}
                  </Typography>
                )}
              </Alert>

              {/* Error Details (Development/Debug Mode) */}
              {(isDevelopment() || isDebugEnabled()) && this.state.error && (
                <Alert severity="warning">
                  <Typography variant="subtitle2" gutterBottom>
                    {__('Debug Information:')}
                  </Typography>
                  <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                    {this.state.error.message}
                  </Typography>
                  {this.state.error.stack && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                        {this.state.error.stack.split('\n').slice(0, 5).join('\n')}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  onClick={this.handleRetry}
                  sx={{ flex: 1 }}
                >
                  {__('Try Again')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleReload}
                  sx={{ flex: 1 }}
                >
                  {__('Reload Page')}
                </Button>
                {(isDevelopment() || isDebugEnabled()) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={this.copyErrorDetails}
                  >
                    {__('Copy Error Details')}
                  </Button>
                )}
              </Stack>

              {/* Support Information */}
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                {getContactSupportMessage()}{' '}
                <Box
                  component="span"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => window.open(SUPPORT_PHONE_TEL)}
                >
                  {this.props.supportPhoneDisplay || SUPPORT_PHONE_DISPLAY}
                </Box>
              </Typography>
            </Stack>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 